import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Plus, Mail, Store, Megaphone, ShieldCheck, Send, RefreshCw, Crown, MapPin } from "lucide-react";
import { toast } from "sonner";

const BUSINESS_PLANS = [
  { value: "business_free", label: "Business Free" },
  { value: "business_pro", label: "Business Pro" },
  { value: "founding_business", label: "Founding Business" },
];

function statusLabel(b) {
  if (b.status === "suspended") return { text: "Suspended", cls: "bg-rose-100 text-rose-700" };
  if (b.account_status === "pending_activation") return { text: "Pending Activation", cls: "bg-amber-100 text-amber-700" };
  if (b.account_status === "pending_terms" || (b.created_by_admin && !b.terms_accepted)) return { text: "Pending Terms", cls: "bg-indigo-100 text-indigo-700" };
  return { text: "Active", cls: "bg-emerald-100 text-emerald-700" };
}

const EMPTY_BIZ = { business_name: "", contact_name: "", email: "", category: "", location: "", website: "", instagram: "" };
const EMPTY_OPP = { title: "", category: "", location: "", description: "", compensation_type: "paid", payment_amount: "", gift_description: "", creators_needed: 1, platforms: [], niches: [], deliverables: "", deadline: "" };

export function AdminBusinesses({ businesses, setBusinesses, reload, setPlan, setStatus, onViewAiHistory }) {
  const [meta, setMeta] = useState({ business_categories: [], locations: [], platforms: [], niches: [] });
  const [createOpen, setCreateOpen] = useState(false);
  const [biz, setBiz] = useState(EMPTY_BIZ);
  const [savingBiz, setSavingBiz] = useState(false);

  const [oppFor, setOppFor] = useState(null); // business object
  const [opp, setOpp] = useState(EMPTY_OPP);
  const [savingOpp, setSavingOpp] = useState(false);
  const [resending, setResending] = useState("");

  // --- Per-business opportunities panel (view + admin-fix publish) ---
  const [oppPanel, setOppPanel] = useState(null);
  const [bizOpps, setBizOpps] = useState([]);
  const [oppsLoading, setOppsLoading] = useState(false);
  const [fixing, setFixing] = useState("");

  const loadBizOpps = async (b) => {
    if (oppPanel === b.id) { setOppPanel(null); return; }
    setOppPanel(b.id); setOppsLoading(true); setBizOpps([]);
    try { const r = await api.get(`/admin/businesses/${b.id}/opportunities`); setBizOpps(r.data.opportunities || []); }
    catch { toast.error("Could not load opportunities"); }
    finally { setOppsLoading(false); }
  };
  const forcePublish = async (b, o) => {
    setFixing(o.id);
    try {
      const r = await api.post(`/admin/opportunities/${o.id}/force-publish`);
      toast.success("Opportunity set Live (recorded as an admin correction)");
      setBizOpps((list) => list.map((x) => x.id === o.id ? r.data : x));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not set live"); }
    finally { setFixing(""); }
  };

  // --- Plan management ---
  const [planFor, setPlanFor] = useState(null);
  const [planData, setPlanData] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planAction, setPlanAction] = useState("comp_pro");
  const [planDays, setPlanDays] = useState(30);
  const [planExpiry, setPlanExpiry] = useState("");
  const [planReason, setPlanReason] = useState("");
  const [planSaving, setPlanSaving] = useState(false);

  const openPlan = async (b) => {
    setPlanFor(b); setPlanData(null); setPlanLoading(true); setPlanReason(""); setPlanAction("comp_pro"); setAiUsage(null);
    try {
      const [p, a] = await Promise.all([
        api.get(`/admin/businesses/${b.id}/plan`),
        api.get(`/admin/businesses/${b.id}/ai-usage`).catch(() => ({ data: null })),
      ]);
      setPlanData(p.data); setAiUsage(a.data);
    } catch { toast.error("Could not load plan"); }
    finally { setPlanLoading(false); }
  };
  const [aiUsage, setAiUsage] = useState(null);
  const [aiNewUsed, setAiNewUsed] = useState(0);
  const [bonusAmount, setBonusAmount] = useState(1);
  const [bonusReason, setBonusReason] = useState("");
  const [bonusSaving, setBonusSaving] = useState(false);
  const refreshAiUsage = async () => { const a = await api.get(`/admin/businesses/${planFor.id}/ai-usage`); setAiUsage(a.data); };
  const adjustBonus = async (action) => {
    if (!bonusReason.trim()) { toast.error("Add a reason/note for the bonus adjustment"); return; }
    if (!(Number(bonusAmount) > 0)) { toast.error("Enter a positive whole number"); return; }
    setBonusSaving(true);
    try {
      const r = await api.post(`/admin/businesses/${planFor.id}/bonus-credit`, { amount: Number(bonusAmount), reason: bonusReason.trim(), action });
      toast.success(action === "remove" ? `Removed ${r.data.removed} bonus credit(s)` : `Granted ${bonusAmount} bonus credit(s)`);
      if (r.data.warning) toast.warning(r.data.warning, { duration: 8000 });
      setBonusReason("");
      await refreshAiUsage();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not adjust bonus credits"); }
    finally { setBonusSaving(false); }
  };
  const saveAiUsage = async () => {
    if (!planReason.trim()) { toast.error("Add a reason for the usage change"); return; }
    try {
      await api.post(`/admin/businesses/${planFor.id}/ai-usage`, { used: Number(aiNewUsed), reason: planReason.trim() });
      toast.success("AI usage updated and recorded in the audit log");
      await refreshAiUsage();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update usage"); }
  };
  const savePlan = async () => {
    if (!planReason.trim()) { toast.error("Please add a reason/note"); return; }
    setPlanSaving(true);
    try {
      const body = { action: planAction, reason: planReason.trim() };
      if (planAction === "extend_trial") body.days = Number(planDays);
      if (planAction === "set_expiry") body.expiry = planExpiry;
      await api.post(`/admin/businesses/${planFor.id}/plan`, body);
      toast.success("Plan updated and recorded in the audit log");
      const r = await api.get(`/admin/businesses/${planFor.id}/plan`); setPlanData(r.data); setPlanReason("");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update plan"); }
    finally { setPlanSaving(false); }
  };
  const setFounding = async (val) => {
    setPlanSaving(true);
    try {
      await api.post(`/admin/businesses/${planFor.id}/plan`, { action: "set_founding", founding: val, reason: planReason.trim() || `Founding ${val ? "added" : "removed"} by admin` });
      toast.success(`Founding badge ${val ? "added" : "removed"}`);
      const r = await api.get(`/admin/businesses/${planFor.id}/plan`); setPlanData(r.data);
    } catch (e) { toast.error("Could not update badge"); }
    finally { setPlanSaving(false); }
  };
  const fmtD = (s) => { try { return s ? new Date(s).toLocaleDateString() : "—"; } catch { return "—"; } };

  // --- Email diagnostics (mobile-friendly) ---
  const [diagOpen, setDiagOpen] = useState(false);
  const [emailCfg, setEmailCfg] = useState(null);
  const [emailBusy, setEmailBusy] = useState("");
  const [testTo, setTestTo] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [resendTo, setResendTo] = useState("");
  const [resendResult, setResendResult] = useState(null);

  // --- Location correction: The Little Gym Camberley ---
  const [lgOpen, setLgOpen] = useState(false);
  const [lgData, setLgData] = useState(null);
  const [lgBusy, setLgBusy] = useState(false);
  const [lgOppId, setLgOppId] = useState("");
  const [lgResult, setLgResult] = useState(null);
  const loadLittleGym = async () => {
    setLgBusy(true); setLgResult(null);
    try {
      const r = await api.get("/admin/corrections/little-gym"); setLgData(r.data);
      if (r.data.found && r.data.opportunities?.length === 1) setLgOppId(r.data.opportunities[0].id);
    } catch (e) { toast.error(e.response?.status === 404 ? "Deploy this build first — the correction endpoint isn't live yet." : "Could not load the correction preview"); }
    finally { setLgBusy(false); }
  };
  const applyLittleGym = async () => {
    if (!lgOppId) { toast.error("Select which opportunity to correct"); return; }
    if (!window.confirm("Apply the Camberley location correction to the selected opportunity and the business profile?")) return;
    setLgBusy(true);
    try {
      const r = await api.post("/admin/corrections/little-gym", { opportunity_id: lgOppId });
      setLgResult(r.data);
      toast.success(r.data.already_correct ? "Already correct — no changes needed" : "Correction applied and recorded in the audit log");
      await loadLittleGym();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not apply the correction"); }
    finally { setLgBusy(false); }
  };


  const checkEmailCfg = async () => {
    setEmailBusy("cfg");
    try { const r = await api.get("/admin/email-config-check"); setEmailCfg(r.data); }
    catch (e) { toast.error(e.response?.status === 404 ? "Deploy this build first — the diagnostics endpoint isn't live yet." : "Could not read email config"); }
    finally { setEmailBusy(""); }
  };
  const sendTest = async () => {
    if (!testTo) { toast.error("Enter an email address"); return; }
    setEmailBusy("test"); setTestResult(null);
    try { const r = await api.post("/admin/email-test", { to: testTo }); setTestResult(r.data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Test failed"); }
    finally { setEmailBusy(""); }
  };
  const resendByEmail = async () => {
    if (!resendTo) { toast.error("Enter the business email"); return; }
    setEmailBusy("resend"); setResendResult(null);
    try { const r = await api.post("/admin/businesses/resend-activation-by-email", { email: resendTo }); setResendResult(r.data); if (r.data.resent) toast.success("Activation invite re-sent"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not resend"); }
    finally { setEmailBusy(""); }
  };

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const createBusiness = async () => {
    if (!biz.business_name || !biz.email) { toast.error("Business name and email are required"); return; }
    setSavingBiz(true);
    try {
      const r = await api.post("/admin/businesses", biz);
      toast.success("Business created — activation email sent");
      setCreateOpen(false); setBiz(EMPTY_BIZ);
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create business"); }
    finally { setSavingBiz(false); }
  };

  const resend = async (b) => {
    setResending(b.id);
    try { await api.post(`/admin/businesses/${b.id}/resend-activation`); toast.success("Activation email re-sent"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not resend"); }
    finally { setResending(""); }
  };

  const openOpp = (b) => {
    setOppFor(b);
    setOpp({ ...EMPTY_OPP, category: b.category || "", location: b.location || "" });
  };

  const togglePlatform = (p) => setOpp((o) => ({ ...o, platforms: o.platforms.includes(p) ? o.platforms.filter((x) => x !== p) : [...o.platforms, p] }));
  const toggleNiche = (n) => setOpp((o) => ({ ...o, niches: o.niches.includes(n) ? o.niches.filter((x) => x !== n) : [...o.niches, n] }));

  const createOpp = async () => {
    if (!opp.title) { toast.error("Give the opportunity a title"); return; }
    setSavingOpp(true);
    try {
      const payload = {
        business_user_id: oppFor.id,
        title: opp.title, category: opp.category, location: opp.location, description: opp.description,
        compensation_type: opp.compensation_type,
        payment_amount: opp.compensation_type === "gifted" ? null : (opp.payment_amount === "" ? null : Number(opp.payment_amount)),
        gift_description: opp.compensation_type === "paid" ? "" : opp.gift_description,
        creators_needed: Number(opp.creators_needed) || 1,
        platforms: opp.platforms, niches: opp.niches, deliverables: opp.deliverables, deadline: opp.deadline,
      };
      await api.post("/admin/opportunities", payload);
      toast.success("Draft opportunity created — the business must approve it to publish");
      setOppFor(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create opportunity"); }
    finally { setSavingOpp(false); }
  };

  return (
    <div className="mt-6 space-y-3">
      {/* Email diagnostics — mobile-friendly, tap-only */}
      <div className="rounded-2xl border border-indigo-200 bg-indigo-50/60 p-4" data-testid="email-diagnostics-card">
        <button data-testid="email-diag-toggle" onClick={() => { setDiagOpen((v) => !v); if (!emailCfg) checkEmailCfg(); }}
          className="flex w-full items-center justify-between gap-2 text-left">
          <span className="flex items-center gap-2 font-display font-bold text-indigo-900"><Mail className="h-4 w-4" /> Email delivery diagnostics</span>
          <span className="text-xs font-semibold text-indigo-700">{diagOpen ? "Hide" : "Open"}</span>
        </button>
        {diagOpen && (
          <div className="mt-3 space-y-4">
            {/* Config */}
            <div className="rounded-xl border border-indigo-100 bg-white p-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-zinc-800">Live email configuration</p>
                <Button size="sm" variant="outline" data-testid="email-cfg-refresh" disabled={emailBusy === "cfg"} onClick={checkEmailCfg} className="rounded-full">
                  {emailBusy === "cfg" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                </Button>
              </div>
              {emailCfg ? (
                <div className="mt-2 space-y-1.5 text-sm" data-testid="email-cfg-result">
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${emailCfg.will_send_real_emails ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                      {emailCfg.will_send_real_emails ? "Real sending ENABLED" : "NOT sending real emails"}
                    </span>
                    {emailCfg.email_test_mode && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">TEST MODE ON</span>}
                  </div>
                  <div className="flex justify-between"><span className="text-zinc-500">Key fingerprint</span><span className="font-mono text-zinc-900">{emailCfg.resend_api_key_present ? emailCfg.resend_api_key_fingerprint : "missing"}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Sender</span><span className="text-zinc-900">{emailCfg.sender_email}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Resend library</span><span className="text-zinc-900">{emailCfg.resend_lib_installed ? "installed" : "missing"}</span></div>
                  <p className="pt-1 text-xs text-zinc-500">Sender should be <b>no-reply@nearfluence.co.uk</b>. If a key is missing or test mode is ON, that’s the cause — update the backend env and redeploy.</p>
                </div>
              ) : <p className="mt-2 text-sm text-zinc-500">Tap refresh to load. (Requires this build to be deployed.)</p>}
            </div>

            {/* Send test */}
            <div className="rounded-xl border border-indigo-100 bg-white p-3">
              <p className="text-sm font-semibold text-zinc-800">Send a delivery test</p>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                <Input data-testid="email-test-to" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="name@example.com" className="h-10" />
                <Button data-testid="email-test-send" disabled={emailBusy === "test"} onClick={sendTest} className="rounded-full bg-indigo-600 hover:bg-indigo-700">
                  {emailBusy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-1 h-3.5 w-3.5" /> Send test</>}
                </Button>
              </div>
              {testResult && (
                <p className="mt-2 text-sm" data-testid="email-test-result">
                  {testResult.result?.ok ? <span className="text-emerald-700">Accepted by Resend · id <span className="font-mono">{testResult.result.resend_id}</span></span>
                    : testResult.result?.attempted === false ? <span className="text-rose-700">Not sent — {testResult.result.reason}</span>
                    : <span className="text-rose-700">Error — {testResult.result?.error}</span>}
                </p>
              )}
            </div>

            {/* Resend activation by email */}
            <div className="rounded-xl border border-indigo-100 bg-white p-3">
              <p className="text-sm font-semibold text-zinc-800">Resend a business activation invite</p>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                <Input data-testid="email-resend-to" type="email" value={resendTo} onChange={(e) => setResendTo(e.target.value)} placeholder="business email (e.g. vidaclinicberkshire@gmail.com)" className="h-10" />
                <Button data-testid="email-resend-send" disabled={emailBusy === "resend"} onClick={resendByEmail} className="rounded-full bg-emerald-600 hover:bg-emerald-700">
                  {emailBusy === "resend" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="mr-1 h-3.5 w-3.5" /> Resend invite</>}
                </Button>
              </div>
              {resendResult && (
                <p className="mt-2 text-sm" data-testid="email-resend-result">
                  {resendResult.resent === false ? <span className="text-amber-700">{resendResult.detail || "Not pending activation."}</span>
                    : resendResult.email_result?.ok ? <span className="text-emerald-700">Invite re-sent · Resend id <span className="font-mono">{resendResult.email_result.resend_id}</span> (check Resend → Emails). Draft opportunity preserved.</span>
                    : resendResult.email_result?.attempted === false ? <span className="text-rose-700">Not sent — {resendResult.email_result.reason}</span>
                    : <span className="text-rose-700">Error — {resendResult.email_result?.error}</span>}
                </p>
              )}
              <p className="mt-2 text-xs text-zinc-500">Never reveals the token or a password — it emails a single-use link so the business sets their own password. Account & draft opportunity are preserved.</p>
            </div>
          </div>
        )}
      </div>

      {/* Location correction — The Little Gym Camberley */}
      <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4" data-testid="little-gym-correction-card">
        <button data-testid="lg-toggle" onClick={() => { setLgOpen(!lgOpen); if (!lgOpen && !lgData) loadLittleGym(); }} className="flex w-full items-center justify-between">
          <span className="flex items-center gap-2 font-display font-bold text-amber-900"><MapPin className="h-4 w-4" /> Location fix: The Little Gym Camberley</span>
          <span className="text-xs text-amber-700">{lgOpen ? "Hide" : "Open"}</span>
        </button>
        {lgOpen && (
          <div className="mt-3 space-y-3">
            <div className="flex items-center gap-2">
              <Button size="sm" data-testid="lg-reload" variant="outline" onClick={loadLittleGym} disabled={lgBusy} className="rounded-full">{lgBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />} Load preview</Button>
              <span className="text-xs text-amber-700">Finds camberley@thelittlegym.co.uk, validates GU15 3PT, re-centres its 10-mile radius on Camberley.</span>
            </div>

            {lgData && !lgData.found && (
              <p data-testid="lg-not-found" className="rounded-lg border border-amber-200 bg-white p-3 text-sm text-amber-800">{lgData.message}</p>
            )}

            {lgData && lgData.found && (
              <div className="rounded-xl border border-amber-100 bg-white p-3 text-sm">
                <div className="grid grid-cols-2 gap-1 text-xs text-zinc-600">
                  <span>Business: <b>{lgData.business.business_name}</b></span>
                  <span>Current town: <b>{lgData.business.current_location || "—"}</b></span>
                  <span>Signature postcode: <b>{lgData.signature_postcode}</b></span>
                  <span>Validated: <b>{lgData.postcode_validation ? `${lgData.postcode_validation.lat.toFixed(4)}, ${lgData.postcode_validation.lng.toFixed(4)}` : (lgData.postcode_error || "—")}</b></span>
                </div>
                {lgData.conflict && <p className="mt-2 rounded bg-amber-100 px-2 py-1 text-xs text-amber-800" data-testid="lg-conflict">⚠ {lgData.conflict}</p>}
                <p className="mt-3 text-xs font-semibold text-zinc-700">Choose the opportunity to correct → Camberley, Surrey (radius 10 miles):</p>
                <div className="mt-1.5 space-y-1.5" data-testid="lg-opps">
                  {lgData.opportunities.length === 0 && <p className="text-xs text-zinc-400">No opportunities found for this business.</p>}
                  {lgData.opportunities.map((o) => (
                    <label key={o.id} className={`flex cursor-pointer items-center gap-2 rounded-lg border p-2 text-xs ${lgOppId === o.id ? "border-amber-400 bg-amber-50" : "border-zinc-200"}`}>
                      <input type="radio" name="lg-opp" data-testid={`lg-opp-${o.id}`} checked={lgOppId === o.id} onChange={() => setLgOppId(o.id)} />
                      <span className="min-w-0 flex-1"><b className="text-zinc-800">{o.title || "Untitled"}</b> · {o.location} · radius {o.radius || "—"} · <span className="capitalize">{o.status}</span></span>
                    </label>
                  ))}
                </div>
                <Button data-testid="lg-apply" onClick={applyLittleGym} disabled={lgBusy || !lgOppId} className="mt-3 rounded-full bg-amber-600 hover:bg-amber-700">
                  {lgBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <MapPin className="mr-1 h-4 w-4" />} Apply correction
                </Button>
                {lgResult && (
                  <div data-testid="lg-result" className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-900">
                    {lgResult.already_correct ? "Already correct — no changes made." : <>Corrected. Opportunity <b>{lgResult.after.opportunity.location}</b> ({lgResult.after.opportunity.lat?.toFixed(4)}, {lgResult.after.opportunity.lng?.toFixed(4)}), radius {lgResult.after.opportunity.radius} miles. ID/status/applications preserved.</>}
                  </div>
                )}
                <p className="mt-2 text-[11px] text-zinc-400">Repeat-safe. Records before/after in the admin audit log. No notifications are sent. A radius is a matching distance, not a franchise-territory guarantee.</p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-zinc-500">Onboard businesses and prepare their campaigns. Businesses set their own password and accept the Terms before anything goes live.</p>
        <Button data-testid="admin-create-business-btn" onClick={() => setCreateOpen(true)} className="rounded-full bg-emerald-600 hover:bg-emerald-700"><Plus className="mr-1 h-4 w-4" /> Create business</Button>
      </div>

      {businesses.map((b) => {
        const sl = statusLabel(b);
        return (
          <div key={b.id} data-testid={`admin-business-${b.id}`} className="rounded-2xl border border-zinc-200 bg-white p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-zinc-100 text-zinc-400"><Store className="h-5 w-5" /></div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-display font-semibold text-zinc-900">{b.business_name || b.name}</p>
                <p className="truncate text-sm text-zinc-500">{b.email}{b.contact_name ? ` · ${b.contact_name}` : ""} · {b.category || "—"} · {b.location || "—"}</p>
              </div>
              <span data-testid={`admin-business-status-${b.id}`} className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${sl.cls}`}>{sl.text}</span>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-zinc-100 pt-3">
              <Button size="sm" variant="outline" data-testid={`admin-add-opp-${b.id}`} onClick={() => openOpp(b)} className="rounded-full"><Megaphone className="mr-1 h-3.5 w-3.5" /> Create opportunity</Button>
              <Button size="sm" variant="outline" data-testid={`admin-view-opps-${b.id}`} onClick={() => loadBizOpps(b)} className="rounded-full">{oppPanel === b.id ? "Hide opportunities" : "Opportunities"}</Button>
              <Button size="sm" variant="outline" data-testid={`admin-manage-plan-${b.id}`} onClick={() => openPlan(b)} className="rounded-full"><Crown className="mr-1 h-3.5 w-3.5" /> Manage plan</Button>
              {b.account_status === "pending_activation" && (
                <Button size="sm" variant="outline" data-testid={`admin-resend-activation-${b.id}`} disabled={resending === b.id} onClick={() => resend(b)} className="rounded-full border-amber-200 text-amber-700 hover:bg-amber-50">
                  {resending === b.id ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Mail className="mr-1 h-3.5 w-3.5" />} Resend activation
                </Button>
              )}
              <div className="ml-auto flex items-center gap-2">
                <Select value={b.plan || "business_free"} onValueChange={(v) => setPlan(b.id, v, businesses, setBusinesses)}>
                  <SelectTrigger data-testid={`admin-plan-${b.id}`} className="h-9 w-44"><SelectValue /></SelectTrigger>
                  <SelectContent>{BUSINESS_PLANS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" variant="outline" data-testid={`admin-suspend-${b.id}`}
                  onClick={() => setStatus(b.id, b.status === "suspended" ? "active" : "suspended", businesses, setBusinesses)}
                  className={b.status === "suspended" ? "rounded-full" : "rounded-full border-rose-200 text-rose-600 hover:bg-rose-50"}>
                  {b.status === "suspended" ? "Reactivate" : "Suspend"}
                </Button>
              </div>
            </div>
            {oppPanel === b.id && (
              <div className="mt-3 border-t border-zinc-100 pt-3" data-testid={`admin-opps-panel-${b.id}`}>
                {oppsLoading ? <div className="flex justify-center py-3"><Loader2 className="h-4 w-4 animate-spin text-zinc-400" /></div>
                  : bizOpps.length === 0 ? <p className="text-sm text-zinc-500">No opportunities yet.</p>
                  : bizOpps.map((o) => {
                    const live = o.status === "published" && o.approval_state === "approved";
                    const awaiting = o.approval_state === "awaiting_business_approval";
                    return (
                      <div key={o.id} data-testid={`admin-opp-row-${o.id}`} className="flex flex-wrap items-center gap-2 rounded-xl border border-zinc-100 bg-zinc-50 p-2.5">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-zinc-800">{o.title || "Untitled"}</p>
                          <p className="text-xs text-zinc-500">
                            {live ? "Live" : o.status === "published" ? "Published" : "Draft"}
                            {awaiting && " · awaiting business approval"}
                            {o.admin_corrected && " · admin-corrected"}
                            {` · ${o.visibility === "direct" ? "invite only" : o.visibility === "both" ? "public + invites" : "public"}`}
                          </p>
                        </div>
                        {live
                          ? <span data-testid={`admin-opp-live-${o.id}`} className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">Live</span>
                          : <Button size="sm" data-testid={`admin-force-publish-${o.id}`} disabled={fixing === o.id} onClick={() => forcePublish(b, o)} className="rounded-full bg-emerald-600 hover:bg-emerald-700">
                              {fixing === o.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Set Live (admin fix)"}
                            </Button>}
                      </div>
                    );
                  })}
                {bizOpps.some((o) => o.status === "published" && o.approval_state === "approved") && (
                  <p className="mt-2 text-xs text-zinc-400">Live opportunities are public at <span className="font-mono">/opportunities/&lt;id&gt;</span>.</p>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Create business dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="create-business-dialog">
          <DialogHeader>
            <DialogTitle>Create a business account</DialogTitle>
            <DialogDescription>We’ll email a secure, single-use activation link. The business sets its own password and accepts the Terms.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Business name *</Label><Input data-testid="cb-business-name" value={biz.business_name} onChange={(e) => setBiz({ ...biz, business_name: e.target.value })} className="mt-1.5" placeholder="The Corner Café" /></div>
            <div><Label>Contact name</Label><Input data-testid="cb-contact-name" value={biz.contact_name} onChange={(e) => setBiz({ ...biz, contact_name: e.target.value })} className="mt-1.5" placeholder="Jane Smith" /></div>
            <div><Label>Email *</Label><Input data-testid="cb-email" type="email" value={biz.email} onChange={(e) => setBiz({ ...biz, email: e.target.value })} className="mt-1.5" placeholder="owner@business.com" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Category</Label>
                <Select value={biz.category} onValueChange={(v) => setBiz({ ...biz, category: v })}>
                  <SelectTrigger data-testid="cb-category" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{(meta.business_categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Location</Label>
                <Select value={biz.location} onValueChange={(v) => setBiz({ ...biz, location: v })}>
                  <SelectTrigger data-testid="cb-location" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{(meta.locations || []).map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Website (optional)</Label><Input data-testid="cb-website" value={biz.website} onChange={(e) => setBiz({ ...biz, website: e.target.value })} className="mt-1.5" placeholder="https://…" /></div>
            <div><Label>Instagram (optional)</Label><Input data-testid="cb-instagram" value={biz.instagram} onChange={(e) => setBiz({ ...biz, instagram: e.target.value })} className="mt-1.5" placeholder="@handle" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="cb-submit" onClick={createBusiness} disabled={savingBiz} className="rounded-full bg-emerald-600 hover:bg-emerald-700">
              {savingBiz ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create & send invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create opportunity (draft) dialog */}
      <Dialog open={!!oppFor} onOpenChange={(v) => !v && setOppFor(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="create-opp-dialog">
          <DialogHeader>
            <DialogTitle>Draft an opportunity</DialogTitle>
            <DialogDescription>For <b>{oppFor?.business_name || oppFor?.name}</b>. Saved as “Draft — awaiting business approval”. It stays hidden until the business reviews and approves it.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Title *</Label><Input data-testid="co-title" value={opp.title} onChange={(e) => setOpp({ ...opp, title: e.target.value })} className="mt-1.5" placeholder="Promote our new weekend brunch" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Category</Label>
                <Select value={opp.category} onValueChange={(v) => setOpp({ ...opp, category: v })}>
                  <SelectTrigger data-testid="co-category" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{(meta.business_categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Location</Label>
                <Select value={opp.location} onValueChange={(v) => setOpp({ ...opp, location: v })}>
                  <SelectTrigger data-testid="co-location" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{(meta.locations || []).map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Description</Label><Textarea data-testid="co-description" value={opp.description} onChange={(e) => setOpp({ ...opp, description: e.target.value })} className="mt-1.5" rows={3} placeholder="What are you looking for?" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Compensation</Label>
                <Select value={opp.compensation_type} onValueChange={(v) => setOpp({ ...opp, compensation_type: v })}>
                  <SelectTrigger data-testid="co-comp" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="gifted">Gifted</SelectItem>
                    <SelectItem value="paid_gifted">Paid + Gifted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Creators needed</Label><Input data-testid="co-needed" type="number" min="1" value={opp.creators_needed} onChange={(e) => setOpp({ ...opp, creators_needed: e.target.value })} className="mt-1.5" /></div>
            </div>
            {opp.compensation_type !== "gifted" && (
              <div><Label>Payment amount (£)</Label><Input data-testid="co-payment" type="number" min="0" value={opp.payment_amount} onChange={(e) => setOpp({ ...opp, payment_amount: e.target.value })} className="mt-1.5" placeholder="80" /></div>
            )}
            {opp.compensation_type !== "paid" && (
              <div><Label>Gift description</Label><Input data-testid="co-gift" value={opp.gift_description} onChange={(e) => setOpp({ ...opp, gift_description: e.target.value })} className="mt-1.5" placeholder="Complimentary brunch for two" /></div>
            )}
            <div>
              <Label>Platforms</Label>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {(meta.platforms || []).map((p) => (
                  <button key={p} type="button" data-testid={`co-platform-${p}`} onClick={() => togglePlatform(p)}
                    className={`rounded-full border px-3 py-1 text-sm capitalize ${opp.platforms.includes(p) ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-zinc-200 text-zinc-600"}`}>{p}</button>
                ))}
              </div>
            </div>
            <div>
              <Label>Categories / niches</Label>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {(meta.niches || []).map((n) => (
                  <button key={n} type="button" data-testid={`co-niche-${n}`} onClick={() => toggleNiche(n)}
                    className={`rounded-full border px-3 py-1 text-sm ${opp.niches.includes(n) ? "border-indigo-600 bg-indigo-50 text-indigo-700" : "border-zinc-200 text-zinc-600"}`}>{n}</button>
                ))}
              </div>
            </div>
            <div><Label>Deliverables</Label><Textarea data-testid="co-deliverables" value={opp.deliverables} onChange={(e) => setOpp({ ...opp, deliverables: e.target.value })} className="mt-1.5" rows={2} placeholder="1 Reel + 2 Stories" /></div>
            <div><Label>Application deadline</Label><Input data-testid="co-deadline" type="date" value={opp.deadline} onChange={(e) => setOpp({ ...opp, deadline: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOppFor(null)} className="rounded-full">Cancel</Button>
            <Button data-testid="co-submit" onClick={createOpp} disabled={savingOpp} className="rounded-full bg-emerald-600 hover:bg-emerald-700">
              {savingOpp ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save draft for approval"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage plan dialog */}
      <Dialog open={!!planFor} onOpenChange={(v) => !v && setPlanFor(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="manage-plan-dialog">
          <DialogHeader>
            <DialogTitle>Manage plan — {planFor?.business_name || planFor?.name}</DialogTitle>
            <DialogDescription>Trials, complimentary Pro and manual overrides. Every change is recorded in the audit log.</DialogDescription>
          </DialogHeader>
          {planLoading || !planData ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div> : (
            <>
              <div className="space-y-1.5 rounded-xl bg-zinc-50 p-3 text-sm" data-testid="plan-current">
                <div className="flex justify-between"><span className="text-zinc-500">Status</span><span className="font-semibold capitalize text-zinc-900">{planData.status.replace(/_/g, " ")}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Signup date</span><span className="text-zinc-900">{fmtD(planData.signup_date)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Trial start</span><span className="text-zinc-900">{fmtD(planData.trial_start)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Trial expiry</span><span className="text-zinc-900">{fmtD(planData.trial_expiry)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Days remaining</span><span className="text-zinc-900">{planData.days_remaining ?? "—"}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Founding badge</span>
                  <button data-testid="plan-toggle-founding" disabled={planSaving} onClick={() => setFounding(!planData.founding)} className={`rounded-full px-2 py-0.5 text-xs font-semibold ${planData.founding ? "bg-amber-100 text-amber-700" : "bg-zinc-200 text-zinc-600"}`}>
                    {planData.founding ? "On — tap to remove" : "Off — tap to add"}
                  </button>
                </div>
              </div>

              <div className="mt-3 space-y-2">
                <div>
                  <Label className="text-xs">Action</Label>
                  <Select value={planAction} onValueChange={setPlanAction}>
                    <SelectTrigger data-testid="plan-action-select" className="mt-1 h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="comp_pro">Upgrade to complimentary Pro</SelectItem>
                      <SelectItem value="reactivate_trial">Reactivate 3-month Pro trial</SelectItem>
                      <SelectItem value="extend_trial">Extend / shorten trial (days)</SelectItem>
                      <SelectItem value="set_expiry">Set specific expiry date</SelectItem>
                      <SelectItem value="downgrade_free">Downgrade to Free</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {planAction === "extend_trial" && (
                  <div><Label className="text-xs">Days (use a negative number to shorten)</Label>
                    <Input data-testid="plan-days" type="number" value={planDays} onChange={(e) => setPlanDays(e.target.value)} className="mt-1 h-10" /></div>
                )}
                {planAction === "set_expiry" && (
                  <div><Label className="text-xs">Expiry date</Label>
                    <Input data-testid="plan-expiry" type="date" value={planExpiry} onChange={(e) => setPlanExpiry(e.target.value)} className="mt-1 h-10" /></div>
                )}
                <div><Label className="text-xs">Reason / internal note *</Label>
                  <Textarea data-testid="plan-reason" value={planReason} onChange={(e) => setPlanReason(e.target.value)} rows={2} className="mt-1" placeholder="Not shown to the business. e.g. Extended trial as a goodwill gesture." /></div>
              </div>

              {aiUsage && (
                <div className="mt-3 rounded-xl border border-indigo-200 bg-indigo-50/50 p-3" data-testid="ai-usage-block">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-indigo-900">AI Marketing credits ({aiUsage.month})</p>
                    {onViewAiHistory && <button data-testid="view-ai-history" onClick={() => { onViewAiHistory(planFor.id); setPlanFor(null); }} className="text-xs font-semibold text-indigo-600 underline">View AI history →</button>}
                  </div>
                  {!aiUsage.is_pro && <p className="mt-1 rounded-lg bg-amber-100 px-2 py-1 text-xs text-amber-800" data-testid="ai-not-entitled">On a Free plan — AI credits can be granted but won't be usable until the business is on Business Pro.</p>}
                  <div className="mt-2 grid grid-cols-2 gap-1 text-xs text-zinc-600">
                    <span>Plan: <b className="capitalize">{(aiUsage.plan || "").replace(/_/g, " ")}</b></span>
                    <span>Monthly allowance: <b>{aiUsage.allowance}</b></span>
                    <span>Used this month: <b>{aiUsage.used_this_month}</b></span>
                    <span>Monthly remaining: <b>{aiUsage.remaining_this_month}</b></span>
                    <span>Bonus available: <b>{aiUsage.bonus_available}</b></span>
                    <span>Reserved (in progress): <b>{aiUsage.reserved_in_progress}</b></span>
                    <span>Total available: <b>{aiUsage.total_available}</b></span>
                    <span>Resets: <b>{fmtD(aiUsage.reset_date)}</b></span>
                  </div>

                  <div className="mt-3 rounded-lg border border-indigo-100 bg-white p-2">
                    <p className="text-xs font-semibold text-zinc-700">Bonus generations (added on top of the monthly allowance; carry over each month)</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Input data-testid="bonus-amount" type="number" min="1" value={bonusAmount} onChange={(e) => setBonusAmount(e.target.value)} className="h-9 w-20" />
                      <Button size="sm" data-testid="bonus-grant" disabled={bonusSaving} onClick={() => adjustBonus("grant")} className="rounded-full bg-emerald-600 hover:bg-emerald-700">Grant</Button>
                      <Button size="sm" variant="outline" data-testid="bonus-remove" disabled={bonusSaving} onClick={() => adjustBonus("remove")} className="rounded-full">Remove unused</Button>
                    </div>
                    <Textarea data-testid="bonus-reason" value={bonusReason} onChange={(e) => setBonusReason(e.target.value)} rows={2} className="mt-2" placeholder="Reason / internal note for this bonus adjustment (required)" />
                  </div>

                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs font-semibold text-zinc-500">Set monthly usage counter (advanced)</summary>
                    <div className="mt-2 flex items-center gap-2">
                      <Input data-testid="ai-usage-set" type="number" min="0" value={aiNewUsed} onChange={(e) => setAiNewUsed(e.target.value)} className="h-9 w-24" />
                      <Button size="sm" data-testid="ai-usage-save" onClick={saveAiUsage} className="rounded-full bg-indigo-600 hover:bg-indigo-700">Set usage (uses reason above)</Button>
                    </div>
                  </details>

                  {Array.isArray(aiUsage.credit_audit) && aiUsage.credit_audit.length > 0 && (
                    <div className="mt-3" data-testid="bonus-audit">
                      <p className="text-xs font-semibold text-zinc-500">Bonus credit history</p>
                      <div className="mt-1 max-h-40 space-y-1 overflow-y-auto">
                        {aiUsage.credit_audit.map((a, i) => (
                          <div key={i} className="rounded-lg border border-zinc-100 bg-zinc-50 p-2 text-xs text-zinc-600">
                            <span className="font-semibold text-zinc-800 capitalize">{a.action} {a.amount}</span>
                            <span> · {a.balance_before} → {a.balance_after}</span>
                            <span className="ml-1 text-zinc-400">{fmtD(a.timestamp)}</span>
                            {a.reason && <p className="mt-0.5">{a.reason}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {Array.isArray(planData.audit) && planData.audit.length > 0 && (
                <div className="mt-3" data-testid="plan-audit">
                  <p className="text-xs font-semibold text-zinc-500">Audit history</p>
                  <div className="mt-1 max-h-40 space-y-1 overflow-y-auto">
                    {planData.audit.map((a, i) => (
                      <div key={i} className="rounded-lg border border-zinc-100 bg-zinc-50 p-2 text-xs text-zinc-600">
                        <span className="font-semibold text-zinc-800">{a.previous_plan} → {a.new_plan}</span>
                        {a.new_expiry && <span> · exp {fmtD(a.new_expiry)}</span>}
                        <span className="ml-1 text-zinc-400">{fmtD(a.timestamp)}</span>
                        {a.reason && <p className="mt-0.5">{a.reason}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <DialogFooter className="mt-3">
                <Button variant="outline" onClick={() => setPlanFor(null)} className="rounded-full">Close</Button>
                <Button data-testid="plan-save" onClick={savePlan} disabled={planSaving} className="rounded-full bg-emerald-600 hover:bg-emerald-700">
                  {planSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply change"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
